/*
 * DevApplicantModal
 * -----------------
 * Test-only modal that lets you add, remove and rename applicants on the
 * /fact-find-test prototype WITHOUT pretending to be a real broker.
 *
 * In production the broker pre-creates applicants when the application is
 * spun up; this modal is just so we can exercise the prototype with 1, 2,
 * 3+ applicants quickly.
 */
import React from 'react';
import { applicantDisplayName } from './steps.config';

class DevApplicantModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { confirmingRemoveId: null };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      this.setState({ confirmingRemoveId: null });
    }
  }

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && this.props.onClose) {
      this.props.onClose();
    }
  };

  handleRename = (id, field, value) => {
    if (this.props.onRename) this.props.onRename(id, field, value);
  };

  handleRemove = (id) => {
    if (this.state.confirmingRemoveId === id) {
      if (this.props.onRemove) this.props.onRemove(id);
      this.setState({ confirmingRemoveId: null });
    } else {
      this.setState({ confirmingRemoveId: id });
    }
  };

  render() {
    const { open, onClose, applicants, onAdd } = this.props;
    if (!open) return null;
    const canRemove = applicants.length > 1;

    return (
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
        style={{ backgroundColor: 'rgba(15, 17, 26, 0.55)', backdropFilter: 'blur(2px)' }}
        onClick={this.handleBackdropClick}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] flex flex-col"
          style={{ border: '1px solid #E5E7EB' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[#E5E7EB]">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#5B3FE0' }}>
                Dev tools
              </p>
              <h2 className="text-base sm:text-lg font-bold text-[#1F2937]">
                Manage applicants
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-[#F5F6FA]"
              aria-label="Close dev tools"
            >
              <svg className="h-5 w-5 text-[#6B7280]" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
            <p className="text-xs text-[#6B7280] mb-4">
              In production the broker pre-creates applicants when the application is set up.
              This modal is here so you can quickly test the form with different applicant
              configurations.
            </p>

            <ul className="space-y-3">
              {applicants.map((applicant, idx) => {
                const confirming = this.state.confirmingRemoveId === applicant.id;
                return (
                  <li
                    key={applicant.id}
                    className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-3"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold flex-shrink-0"
                        style={{ backgroundColor: '#7C5CFC', color: '#FFFFFF' }}
                      >
                        {idx + 1}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] truncate">
                        {applicantDisplayName(applicant, idx)}
                      </span>
                      <span className="ml-auto text-[10px] text-[#9CA3AF] font-mono truncate max-w-[80px] sm:max-w-[140px]">
                        {applicant.id.slice(0, 8)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                          First name
                        </label>
                        <input
                          type="text"
                          value={applicant.given_name || ''}
                          onChange={(e) => this.handleRename(applicant.id, 'given_name', e.target.value)}
                          placeholder="-"
                          className="w-full rounded-md border bg-white px-2 py-1.5 text-sm text-[#1F2937] focus:outline-none focus:border-[#7C5CFC] focus:ring-2 focus:ring-[#7C5CFC]"
                          style={{ borderColor: '#D1D5DB' }}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
                          Last name
                        </label>
                        <input
                          type="text"
                          value={applicant.family_name || ''}
                          onChange={(e) => this.handleRename(applicant.id, 'family_name', e.target.value)}
                          placeholder="-"
                          className="w-full rounded-md border bg-white px-2 py-1.5 text-sm text-[#1F2937] focus:outline-none focus:border-[#7C5CFC] focus:ring-2 focus:ring-[#7C5CFC]"
                          style={{ borderColor: '#D1D5DB' }}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="button"
                        disabled={!canRemove}
                        onClick={() => this.handleRemove(applicant.id)}
                        className="text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{
                          color: confirming ? '#FFFFFF' : '#DC2626',
                          backgroundColor: confirming ? '#DC2626' : 'transparent',
                        }}
                      >
                        {confirming ? 'Click again to confirm' : 'Remove'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              onClick={onAdd}
              className="mt-4 w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold"
              style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
            >
              + Add another applicant
            </button>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-6 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-between rounded-b-2xl">
            <p className="text-[10px] text-[#9CA3AF]">
              Dev only - not shown to customers
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: '#7C5CFC' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
}

class DevToolsButton extends React.Component {
  render() {
    const { applicantCount, onOpen } = this.props;
    return (
      <button
        type="button"
        onClick={onOpen}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-lg transition-transform active:scale-95"
        style={{ backgroundColor: '#1A1D2A', border: '1px solid #2A2D3D' }}
        title="Dev tools - add or remove applicants"
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 2a1 1 0 011 1v1.07A6.002 6.002 0 0115.93 9H17a1 1 0 110 2h-1.07A6.002 6.002 0 0110 15.93V17a1 1 0 11-2 0v-1.07A6.002 6.002 0 014.07 11H3a1 1 0 110-2h1.07A6.002 6.002 0 019 4.07V3a1 1 0 011-1z" />
        </svg>
        <span>Dev</span>
        <span
          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold"
          style={{ backgroundColor: '#7C5CFC', color: '#FFFFFF' }}
        >
          {applicantCount}
        </span>
      </button>
    );
  }
}

export { DevToolsButton };
export default DevApplicantModal;
